import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { apiRequest } from '../../utils/api';
import { formatDateForDisplay } from '../../utils/dateUtils';

interface TaskWorkLog {
  id: number;
  task_id: number;
  user_id: number;
  date: string;
  start_time: string | null;
  end_time: string | null;
  created_at: string | null;
  updated_at: string | null;
  task_title: string | null;
  task_description: string | null;
  task_type: string | null;
}

interface TaskWorkLogDialogProps {
  open: boolean;
  onClose: () => void;
  userId: number;
  date: string;
  onSave: () => void;
}

export default function TaskWorkLogDialog({
  open,
  onClose,
  userId,
  date,
  onSave,
}: TaskWorkLogDialogProps) {
  const [logs, setLogs] = useState<TaskWorkLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingLog, setEditingLog] = useState<TaskWorkLog | null>(null);
  const [startTime, setStartTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');

  // Load task work logs
  useEffect(() => {
    if (open && userId && date) {
      loadLogs();
    }
  }, [open, userId, date]);

  const loadLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiRequest(
        `/working-log/task-work-logs?user_id=${userId}&date=${date}`,
        { method: 'GET' }
      );
      setLogs(response.data?.data || []);
    } catch (err: any) {
      console.error('Error loading task work logs:', err);
      setError(err.response?.data?.message || 'Failed to load task work logs');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (log: TaskWorkLog) => {
    setEditingLog(log);
    // Extract time portion (HH:mm) from datetime
    if (log.start_time) {
      const startDate = new Date(log.start_time);
      const hours = String(startDate.getHours()).padStart(2, '0');
      const minutes = String(startDate.getMinutes()).padStart(2, '0');
      setStartTime(`${hours}:${minutes}`);
    } else {
      setStartTime('');
    }
    if (log.end_time) {
      const endDate = new Date(log.end_time);
      const hours = String(endDate.getHours()).padStart(2, '0');
      const minutes = String(endDate.getMinutes()).padStart(2, '0');
      setEndTime(`${hours}:${minutes}`);
    } else {
      setEndTime('');
    }
  };

  const handleCancelEdit = () => {
    setEditingLog(null);
    setStartTime('');
    setEndTime('');
  };

  const handleSave = async () => {
    if (!editingLog) return;

    try {
      setError(null);
      
      // Combine date with time to create full datetime
      let startDateTime = null;
      if (startTime) {
        const [hours, minutes] = startTime.split(':');
        const [year, month, day] = date.split('-').map(Number);
        const startDate = new Date(year, month - 1, day, parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
        startDateTime = startDate.toISOString();
      }

      let endDateTime = null;
      if (endTime) {
        const [hours, minutes] = endTime.split(':');
        const [year, month, day] = date.split('-').map(Number);
        const endDate = new Date(year, month - 1, day, parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
        endDateTime = endDate.toISOString();
      }

      await apiRequest(`/working-log/task-work-logs/${editingLog.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          start_time: startDateTime,
          end_time: endDateTime,
        }),
      });

      await loadLogs();
      handleCancelEdit();
      onSave();
    } catch (err: any) {
      console.error('Error updating task work log:', err);
      setError(err.response?.data?.message || 'Failed to update task work log');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this task work log?')) {
      return;
    }

    try {
      setError(null);
      await apiRequest(`/working-log/task-work-logs/${id}`, {
        method: 'DELETE',
      });
      await loadLogs();
      onSave();
    } catch (err: any) {
      console.error('Error deleting task work log:', err);
      setError(err.response?.data?.message || 'Failed to delete task work log');
    }
  };

  const calculateHours = (start: string | null, end: string | null): number | null => {
    if (!start || !end) return null;
    try {
      const startTime = new Date(start);
      const endTime = new Date(end);
      const diffMs = endTime.getTime() - startTime.getTime();
      return Math.round((diffMs / (1000 * 60 * 60)) * 100) / 100;
    } catch {
      return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Task Work Logs - {date ? formatDateForDisplay(date) : ''}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {editingLog ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Task: {editingLog.task_title || editingLog.task_description || `Task #${editingLog.task_id}`}
                </Typography>
                <TextField
                  label="Start Time"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="End Time"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <Button onClick={handleCancelEdit} variant="outlined">
                    Cancel
                  </Button>
                  <Button onClick={handleSave} variant="contained">
                    Save
                  </Button>
                </Box>
              </Box>
            ) : (
              <>
                {logs.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    No task work logs found for this date.
                  </Typography>
                ) : (
                  <TableContainer component={Paper} sx={{ mt: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Task</TableCell>
                          <TableCell align="center">Type</TableCell>
                          <TableCell align="center">Start Time</TableCell>
                          <TableCell align="center">End Time</TableCell>
                          <TableCell align="center">Hours</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {logs.map((log) => {
                          const hours = calculateHours(log.start_time, log.end_time);
                          return (
                            <TableRow key={log.id} hover>
                              <TableCell>
                                {log.task_title || log.task_description || `Task #${log.task_id}`}
                              </TableCell>
                              <TableCell align="center">
                                {log.task_type || '-'}
                              </TableCell>
                              <TableCell align="center">
                                {log.start_time
                                  ? new Date(log.start_time).toLocaleTimeString('en-US', {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })
                                  : '-'}
                              </TableCell>
                              <TableCell align="center">
                                {log.end_time
                                  ? new Date(log.end_time).toLocaleTimeString('en-US', {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })
                                  : '-'}
                              </TableCell>
                              <TableCell align="center">
                                {hours !== null ? `${hours.toFixed(1)}h` : '-'}
                              </TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEdit(log)}
                                  color="primary"
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(log.id)}
                                  color="error"
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Total Hours: {logs.reduce((sum, log) => {
                      const hours = calculateHours(log.start_time, log.end_time);
                      return sum + (hours || 0);
                    }, 0).toFixed(1)}h
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
